import React, { useCallback, useEffect, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import './admin.css';

const LABELS = {
  tracking: 'Tracking Backend',
  inventory: 'Inventory Backend',
  supabase: 'Member / Supabase',
  email: 'Restock Email',
};

function statusAppearance(status) {
  if (status === 'operational') return { label: 'Operational', color: '#86efac', border: 'rgba(74,222,128,.42)', background: 'rgba(34,197,94,.13)' };
  if (status === 'warning') return { label: 'Warning', color: '#fde68a', border: 'rgba(251,191,36,.5)', background: 'rgba(245,158,11,.14)' };
  return { label: 'Down', color: '#fca5a5', border: 'rgba(248,113,113,.5)', background: 'rgba(239,68,68,.14)' };
}

function StatusPill({ status }) {
  const style = statusAppearance(status);
  return (
    <span style={{
      border: `1px solid ${style.border}`,
      borderRadius: 999,
      padding: '6px 10px',
      fontSize: 11,
      fontWeight: 800,
      color: style.color,
      background: style.background,
      whiteSpace: 'nowrap',
    }}>
      {style.label}
    </span>
  );
}

function ServiceCard({ name, service }) {
  return (
    <section className="admin-card" style={{ margin: 0 }}>
      <div className="admin-section-head">
        <div>
          <h2>{LABELS[name] || name}</h2>
          <p>{service?.detail || 'No health detail returned.'}</p>
        </div>
        <StatusPill status={service?.status || 'down'} />
      </div>
      <div className="admin-deployment-meta" style={{ marginBottom: 0 }}>
        {Number.isFinite(service?.latencyMs) && <span>Latency: {service.latencyMs} ms</span>}
        {service?.revision && <span>Revision: {service.revision}</span>}
        {service?.atomicRevision !== undefined && <span>Atomic protection: {service.atomicRevision ? 'ACTIVE' : 'NOT DETECTED'}</span>}
        {Number.isFinite(service?.assets) && <span>Assets: {service.assets}</span>}
        {Number.isFinite(service?.catalogItems) && <span>Catalog items: {service.catalogItems}</span>}
        {Number.isFinite(service?.inventoryRecords) && <span>Inventory records: {service.inventoryRecords}</span>}
      </div>
      {service?.checks && (
        <div style={{ display: 'grid', gap: 7, marginTop: 14 }}>
          {Object.entries(service.checks).map(([key, ok]) => (
            <div key={key} className="admin-table-row" style={{ padding: '7px 0' }}>
              <span><strong>{key}</strong></span>
              <StatusPill status={ok ? 'operational' : 'warning'} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function SystemHealth({ profile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getMemberAccessToken();
      if (!token) throw new Error('Member sign in required.');
      const response = await fetch('/api/system-health', {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to load system health.');
      setData(payload);
    } catch (err) {
      setError(err?.message || 'Unable to load system health.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!profile?.is_admin) {
    return (
      <div className="admin-root"><main className="admin-page"><section className="admin-card admin-denied"><h1>Admin access required</h1></section></main></div>
    );
  }

  const overall = data?.overall || (error ? 'down' : 'warning');

  return (
    <div className="admin-root">
      <header className="admin-topbar">
        <div>
          <h1>System Health</h1>
          <p>Live backend, inventory, member access, concurrency, and restock configuration checks</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={load} disabled={loading}>{loading ? 'Checking…' : 'Refresh'}</button>
          <button type="button" onClick={() => window.location.assign('/admin')}>Admin</button>
        </div>
      </header>
      <main className="admin-page">
        <section className="admin-card">
          <div className="admin-section-head">
            <div>
              <h2>Overall</h2>
              <p>{error || (data?.checkedAt ? `Last checked ${new Date(data.checkedAt).toLocaleString()}` : 'Running live checks…')}</p>
            </div>
            <StatusPill status={overall} />
          </div>
        </section>

        {loading && !data ? <div className="admin-empty">Checking services…</div> : null}
        {data?.services ? (
          <div className="admin-two-column">
            {Object.entries(data.services).map(([name, service]) => <ServiceCard key={name} name={name} service={service} />)}
          </div>
        ) : null}
      </main>
    </div>
  );
}
